import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  QrCode, Users, Activity, TrendingUp, Radio, CheckCircle2, XCircle, Download,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import useFetch from '../../hooks/useFetch';
import useChartMotion from '../../hooks/useChartMotion';
import { download } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Card, Stat, Button, Table, Badge, Loader, EmptyState, SectionTitle,
  Tabs, Select, PersonCell, Avatar, SearchInput, label,
} from '../../components/UI';
import { fmtTime } from '../../utils/format';
export default function Attendance() {
  const { eventId, event, events } = useEvents();
  const toast = useToast();
  const [tab, setTab] = useState('live');

  if (!events.length) {
    return <EmptyState message="No events available" hint="Attendance tracking begins once you have an event." icon={QrCode} />;
  }

  return (
    <>
      <SectionTitle
        title="Attendance"
        subtitle="Live QR scans at entry gates, session halls, coaches and the hotel desk"
        action={
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <EventPicker />
            <Button variant="ghost" icon={Download} disabled={!eventId}
              onClick={() => download('/reports/export', { type: 'attendance', event: eventId }, 'attendance.xlsx')
                .then(() => toast.success('Attendance report downloaded'))
                .catch((e) => toast.error(e.message))}>
              Export
            </Button>
          </div>
        }
      />

      <Tabs
        active={tab} onChange={setTab}
        tabs={[
          { key: 'live', label: 'Live feed', icon: Radio },
          { key: 'sessions', label: 'By session', icon: Activity },
          { key: 'people', label: 'By participant', icon: Users },
        ]}
      />

      {tab === 'live' && <LiveFeed eventId={eventId} />}
      {tab === 'sessions' && <SessionReport eventId={eventId} event={event} />}
      {tab === 'people' && <Participation eventId={eventId} />}
    </>
  );
}

/* ── live feed ────────────────────────────────────────────────────────── */
function LiveFeed({ eventId }) {
  const { data, loading, reload } = useFetch('/attendance/live', { event: eventId }, { skip: !eventId });

  /* Refresh every 15s so the ops screen stays honest without a websocket. */
  useEffect(() => {
    if (!eventId) return;
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [eventId, reload]);

  if (loading && !data) return <Loader label="Connecting to the live feed" />;

  const byType = data?.byType || {};
  const TYPES = [
    ['event_entry', 'Event Entry', 'gold'],
    ['session', 'Session Check-ins', 'info'],
    ['bus_boarding', 'Bus Boarding', 'warning'],
    ['hotel_checkin', 'Hotel Check-in', 'success'],
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 13, marginBottom: 18 }}>
        {TYPES.map(([key, text, tone]) => (
          <Stat key={key} label={text} value={byType[key]?.scans ?? 0} icon={QrCode} tone={tone}
            sub={`${byType[key]?.uniquePeople ?? 0} unique people`} />
        ))}
      </div>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <motion.span
              animate={{ opacity: [1, 0.35, 1], scale: [1, 0.85, 1] }}
              transition={{ duration: 1.8, repeat: Infinity }}
              style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)' }}
            />
            <h3 style={{ fontSize: 15, fontWeight: 600 }}>Live scan feed</h3>
          </div>
          <span style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>Refreshes every 15s</span>
        </div>

        {!data?.feed?.length ? (
          <EmptyState message="No scans in the last two hours" hint="Scans appear here the moment your staff start checking people in." icon={Radio} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.feed.map((s, i) => (
              <motion.div
                key={s._id}
                initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.35) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px',
                  background: 'var(--ink-800)', border: '1px solid var(--border)', borderRadius: 'var(--r)',
                }}
              >
                <Avatar name={s.user?.name} size={30} vip={s.user?.isVip} src={s.user?.avatar} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{s.user?.name || 'Unknown'}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>
                    {s.sessionTitle || label(s.scanType)}
                    {s.location ? ` · ${s.location}` : ''}
                    {s.scannedBy?.name ? ` · by ${s.scannedBy.name}` : ''}
                  </div>
                </div>
                {s.isManual && <Badge tone="warning">Manual</Badge>}
                <span style={{ fontSize: 11.5, color: 'var(--text-mid)', whiteSpace: 'nowrap' }}>
                  {fmtTime(s.scannedAt)}
                </span>
              </motion.div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

/* ── per-session report ───────────────────────────────────────────────── */
function SessionReport({ eventId, event }) {
  // Memoised so the effect below does not re-run on every render.
  const sessions = useMemo(() => event?.sessions || [], [event]);
  const [sessionId, setSessionId] = useState('');

  useEffect(() => {
    if (sessions.length && !sessions.some((s) => s._id === sessionId)) setSessionId(sessions[0]._id);
  }, [sessions, sessionId]);

  const { data, loading } = useFetch(
    '/attendance/session-report', { event: eventId, sessionId },
    { skip: !eventId || !sessionId },
  );

  if (!sessions.length) {
    return <EmptyState message="This event has no sessions" hint="Add sessions to the event schedule to track per-session attendance." icon={Activity} />;
  }

  return (
    <>
      <Card padding={15} style={{ marginBottom: 16 }}>
        <Select value={sessionId} onChange={(e) => setSessionId(e.target.value)} style={{ maxWidth: 460 }}>
          {sessions.map((s) => (
            <option key={s._id} value={s._id}>
              {s.title} · {fmtTime(s.startAt)}
            </option>
          ))}
        </Select>
      </Card>

      {loading ? <Loader /> : data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 13, marginBottom: 18 }}>
            <Stat label="Expected" value={data.summary.total} icon={Users} tone="info" />
            <Stat label="Present" value={data.summary.present} icon={CheckCircle2} tone="success" progress={data.summary.percentage} />
            <Stat label="Absent" value={data.summary.absent} icon={XCircle} tone="warning" />
            <Stat label="Attendance" value={`${data.summary.percentage}%`} icon={TrendingUp} tone="gold" progress={data.summary.percentage} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--ok)', marginBottom: 13 }}>
                Present ({data.present.length})
              </h3>
              <PeopleList people={data.present} showTime />
            </Card>
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--warn)', marginBottom: 13 }}>
                Absent ({data.absent.length})
              </h3>
              <PeopleList people={data.absent} />
            </Card>
          </div>
        </>
      )}
    </>
  );
}

function PeopleList({ people, showTime = false }) {
  if (!people?.length) return <p style={{ fontSize: 13, color: 'var(--text-lo)' }}>Nobody in this list.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 380, overflowY: 'auto' }}>
      {people.map((p) => (
        <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 2px' }}>
          <Avatar name={p.name} size={28} vip={p.isVip} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.8, fontWeight: 500 }}>{p.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-lo)' }}>{p.department || p.designation || p.employeeCode}</div>
          </div>
          {showTime && p.scannedAt && (
            <span style={{ fontSize: 11, color: 'var(--text-mid)' }}>
              {fmtTime(p.scannedAt)}{p.isManual ? ' · manual' : ''}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── per-participant participation ────────────────────────────────────── */
function Participation({ eventId }) {
  const [search, setSearch] = useState('');
  const { data, loading } = useFetch('/attendance/participation', { event: eventId }, { skip: !eventId });
  const chartMotion = useChartMotion();

  if (loading) return <Loader label="Building the participation report" />;
  if (!data) return <EmptyState message="No participation data yet" icon={Users} />;

  const rows = (data.rows || []).filter((r) =>
    !search || r.user.name.toLowerCase().includes(search.toLowerCase()));

  const chart = [
    { name: 'High', value: data.summary.highEngagement, fill: '#4ADE80' },
    { name: 'Medium', value: data.summary.participants - data.summary.highEngagement - data.summary.lowEngagement, fill: '#EFD48A' },
    { name: 'Low', value: data.summary.lowEngagement, fill: '#F87171' },
  ];

  const TONE = { high: 'success', medium: 'gold', low: 'danger' };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 13, marginBottom: 18 }}>
        <Stat label="Participants" value={data.summary.participants} icon={Users} tone="info" />
        <Stat label="Avg. Attendance" value={`${data.summary.averageAttendance}%`} icon={TrendingUp} tone="gold"
          progress={data.summary.averageAttendance} />
        <Stat label="Highly Engaged" value={data.summary.highEngagement} icon={CheckCircle2} tone="success" />
        <Stat label="Low Engagement" value={data.summary.lowEngagement} icon={XCircle} tone="danger" />
      </div>

      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 15 }}>Engagement distribution</h3>
        <ResponsiveContainer width="100%" height={190}>
          <BarChart data={chart} layout="vertical" margin={{ left: 6, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#24242B" horizontal={false} />
            <XAxis type="number" tick={{ fill: '#71717F', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <YAxis type="category" dataKey="name" tick={{ fill: '#A8A8B3', fontSize: 12 }} axisLine={false} tickLine={false} width={62} />
            <Tooltip cursor={{ fill: 'rgba(239,212,138,0.06)' }}
              contentStyle={{ background: '#16161A', border: '1px solid #24242B', borderRadius: 12, fontSize: 12 }} />
            <Bar dataKey="value" radius={[0, 7, 7, 0]} maxBarSize={34} {...chartMotion}>
              {chart.map((d) => <Cell key={d.name} fill={d.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <SearchInput value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search participants…" style={{ maxWidth: 350, marginBottom: 16 }} />

      <Table
        rows={rows}
        keyField="user"
        empty="No participants match your search"
        columns={[
          { key: 'user', title: 'Participant', render: (r) => <PersonCell person={r.user} /> },
          { key: 'department', title: 'Department', render: (r) => r.user.department || '—' },
          { key: 'attended', title: 'Sessions', render: (r) => `${r.attended} / ${r.totalSessions}` },
          { key: 'percentage', title: 'Attendance', render: (r) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 130 }}>
              <div style={{ flex: 1, height: 5, background: 'var(--ink-700)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{
                  width: `${r.percentage}%`, height: '100%', borderRadius: 99,
                  background: r.percentage >= 75 ? 'var(--ok)' : r.percentage >= 40 ? 'var(--gold-400)' : 'var(--danger)',
                }} />
              </div>
              <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', minWidth: 34 }}>{r.percentage}%</span>
            </div>
          )},
          { key: 'engagement', title: 'Engagement', render: (r) => (
            <Badge tone={TONE[r.engagement]}>{label(r.engagement)}</Badge>
          )},
        ]}
      />
    </>
  );
}
